import {
    type MutationFunctionContext,
    type UseMutationOptions,
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";
import { api } from "api";
import type { ManualPartialRefundRequest } from "api/requests";
import type { TransactionRefundResult } from "api/responses";

export type PartialRefundOptions = UseMutationOptions<TransactionRefundResult, Error, ManualPartialRefundRequest>;

/**
 * Client hook to perform Provider Customization of various settings.
 * @param options Options for the client call; provides callbacks for when the API call succeeds or fails.
 * @returns React Query mutation properties.
 */
export const useTransactionPartialRefund = (patientId: number, options?: PartialRefundOptions) => {
    const queryClient = useQueryClient();

    /** Provide built-in onSuccess behavior, then call provided callback if available. */
    const onSuccess = (
        data: TransactionRefundResult,
        variables: ManualPartialRefundRequest,
        onMutateResult: unknown,
        context: MutationFunctionContext,
    ) => {
        // Invalidate queries that are affected by refunding a transaction
        void queryClient.invalidateQueries({ queryKey: ["transactionHistory", patientId] });
        void queryClient.invalidateQueries({ queryKey: ["paymentReport"] });

        // Use callback if provided
        return options?.onSuccess?.(data, variables, onMutateResult, context);
    };

    return useMutation({
        ...options,
        mutationFn: async ({ transactionId, amount }: ManualPartialRefundRequest) =>
            await api.transaction.partialRefund(transactionId, amount),
        onSuccess,
    });
};
